import cx from 'classnames';
import * as React from 'react';
import ReactDOM from 'react-dom';
import xhr from 'xhr';

import Link from 'components/Link';
import Glyph from 'components/Glyph';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Dialog from 'components/Dialog';
import MediaObject from 'components/MediaObject';
import TextWithGlyph from 'components/TextWithGlyph';
import Text from 'components/Text';
import LoadingIndicator from 'components/LoadingIndicator';
import Image from 'components/Image';
// @ts-ignore
import trackView from 'lib/trackView';
import TitleHeader from 'components/TitleHeader';
import { MouseEventHandler } from 'types/typescript/eventHandlers';

import './styles/LinkedContentPreviewModal.scss';

type Props = {
  linkedContent: any;
  className?: string;
  onRequestHide?: () => void;
};

type State = {
  numberOfPdfPages: number;
  data?: any;
  dataType?: string | null;
  loading: boolean;
  reactPdf?: any;
};

class LinkedContentPreviewModal extends React.Component<Props, State> {
  state: State = {
    numberOfPdfPages: 0,
    loading: false,
    data: null,
    dataType: null,
    reactPdf: null,
  };

  componentWillMount() {
    this.loadReactPdf();

    const { linkedContent } = this.props;
    const { downloadPreview } = linkedContent;
    if (downloadPreview) {
      const { url, availableMimeTypes } = downloadPreview;
      const pdfType = availableMimeTypes.find((t: string) => t === 'application/pdf');
      const imageType = availableMimeTypes.find((t: string) => t.startsWith('image/'));
      const requestedType = pdfType || imageType;

      if (requestedType) {
        this.setState({
          loading: true,
        });

        xhr(
          {
            method: 'GET',
            uri: url,
            headers: {
              Accept: requestedType,
            },
            withCredentials: true,
            responseType: 'arraybuffer',
          },
          (err: any, resp: { headers: { [x: string]: string | null | undefined } }, body: any) => {
            this.setState({
              data: err ? null : body,
              dataType: err ? null : resp.headers['content-type'],
              loading: false,
            });
          }
        );
      }
    }
  }

  loadReactPdf() {
    // @ts-ignore
    import(/* webpackChunkName: "react-pdf" */ 'react-pdf')
      .then(reactPdf => this.setState({ reactPdf }))
      .catch(e => console.error('could not load react-pdf library', e));
  }

  handleCloseButtonClick = () => {
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  handleModalClick: MouseEventHandler = event => {
    const { target } = event;

    const header = ReactDOM.findDOMNode(this.refs.header);
    if (target instanceof Node && header && header.contains(target)) {
      return;
    }

    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  handleLoadPdf = (pdf: any) => {
    this.setState({
      numberOfPdfPages: pdf.numPages,
    });
  };

  renderNoPreview() {
    return (
      <TitleHeader
        className="LinkedContentPreviewModal-no-preview"
        image={require('assets/images/content_browsing/no_preview_available.svg')}
        imageClassName="LinkedContentPreviewModal-no-preview-image"
        color="whiteout"
        size="small"
        title="No preview available"
      />
    );
  }

  renderContent() {
    const { numberOfPdfPages, data, dataType, loading, reactPdf } = this.state;

    if (loading) {
      return <LoadingIndicator />;
    }

    if (data && dataType) {
      if (dataType === 'application/pdf' && reactPdf) {
        const { Document, Page } = reactPdf;
        return (
          <div>
            <Document file={data} onLoadSuccess={this.handleLoadPdf} loading={<LoadingIndicator />}>
              {Array.from(new Array(numberOfPdfPages), (el, index) => (
                <Page
                  className="LinkedContentPreviewModal-pdf"
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                />
              ))}
            </Document>
          </div>
        );
      } else if (dataType.startsWith('image/')) {
        const blob = new Blob([data], { type: dataType }); // eslint-disable-line
        const objUrl = window.URL.createObjectURL(blob);
        return <Image className="LinkedContentPreviewModal-image" src={objUrl} />;
      }
    }

    return this.renderNoPreview();
  }

  render() {
    const { linkedContent } = this.props;

    const leftMediaObject = (() => {
      if (linkedContent.fileTypeIcon) {
        return (
          <MediaObject center figure="left" gutter="xsmall">
            <Image
              className="LinkedContentPreviewModal-fileTypeIcon"
              src={linkedContent.fileTypeIcon}
            />
            <Text truncate>{linkedContent.title || linkedContent.contentUrl}</Text>
          </MediaObject>
        );
      } else {
        return (
          <TextWithGlyph glyph="image" glyphSize="medium" truncate>
            {linkedContent.title || linkedContent.contentUrl}
          </TextWithGlyph>
        );
      }
    })();

    return (
      <Dialog
        {...this.props}
        box={false}
        hideCloseButton
        full
        theme="dark"
        className={cx('LinkedContentPreviewModal', this.props.className)}
        onClick={this.handleModalClick}>
        <header ref="header" className="LinkedContentPreviewModal-header">
          <MediaObject center figure="right" className="LinkedContentPreviewModal-header-inner">
            {leftMediaObject}
            <Buttons reset="none">
              <Link
                href={linkedContent.contentUrl}
                target="_blank"
                color="whiteout"
                className="LinkedContentPreviewModal-viewOriginalButton">
                <TextWithGlyph glyph="fullscreen" glyphSize="small">
                  View original
                </TextWithGlyph>
              </Link>
              <Button
                aria-label="Close"
                color="whiteout"
                level="icon"
                onClick={this.handleCloseButtonClick}>
                <Glyph name="close" solo />
              </Button>
            </Buttons>
          </MediaObject>
        </header>
        <div className="LinkedContentPreviewModal-wrap">
          <div className="LinkedContentPreviewModal-content">{this.renderContent()}</div>
        </div>
      </Dialog>
    );
  }
}

export { LinkedContentPreviewModal };
export default trackView(LinkedContentPreviewModal, 'LinkedContentPreviewModal');
